import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { Product, PurchaseLink, Brand } from '@hygh-vybe/lib';
import { ProductRatingReview } from '../components/ProductRatingReview';
import { SEO } from '../components/SEO';
import { productSlug } from '../utils/slug';
import './ProductDetailPage.css';
import { API_BASE } from '../config';

interface AffiliateInfo {
  linkId: string;
  trackedUrl: string;
  discountCode?: string;
  discountDescription?: string;
}

interface PurchaseLinksResponse {
  links: PurchaseLink[];
  primaryAffiliate?: AffiliateInfo;
}

export function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [product, setProduct] = useState<Product | null>(null);
  const [purchaseData, setPurchaseData] = useState<PurchaseLinksResponse | null>(null);
  const [brand, setBrand] = useState<Brand | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch product details, purchase links, and reviews
  useEffect(() => {
    if (!id) return;

    const controller = new AbortController();

    async function fetchProduct() {
      try {
        const res = await fetch(`${API_BASE}/v1/products/${id}`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Failed to load product (${res.status})`);
        const json = await res.json();
        setProduct(json.data);
      } catch (err: unknown) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError(err instanceof Error ? err.message : 'Failed to load product');
      }
    }

    async function fetchPurchaseLinks() {
      try {
        const res = await fetch(`${API_BASE}/v1/products/${id}/purchase-links`, {
          signal: controller.signal,
        });
        if (!res.ok) return;
        const json = await res.json();
        const data = json.data ?? {};
        setPurchaseData({
          links: data.purchaseLinks ?? data.links ?? [],
          primaryAffiliate: data.primaryAffiliate,
        });
      } catch {
        // Purchase links are non-critical — fail silently
      }
    }

    setLoading(true);
    setError(null);

    Promise.all([fetchProduct(), fetchPurchaseLinks()]).finally(() => {
      if (!controller.signal.aborted) {
        setLoading(false);
      }
    });

    return () => controller.abort();
  }, [id]);

  // Fetch brand data to get buyWebsiteUrl
  useEffect(() => {
    if (!product) return;
    const brandId = product.brandId;
    if (!brandId) {
      // Try to find brand by name from the brands list
      fetch(`${API_BASE}/v1/brands`)
        .then((res) => res.ok ? res.json() : null)
        .then((json) => {
          if (json?.data) {
            const match = json.data.find((b: Brand) => b.brandName === product.brandName);
            if (match) setBrand(match);
          }
        })
        .catch(() => {});
      return;
    }
    fetch(`${API_BASE}/v1/brands/${brandId}`)
      .then((res) => res.ok ? res.json() : null)
      .then((json) => {
        if (json?.data) setBrand(json.data);
      })
      .catch(() => {});
  }, [product]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handlePurchaseClick = useCallback(
    (linkId: string) => {
      // Record Click_Event via the redirect endpoint
      window.open(`${API_BASE}/v1/go/${linkId}`, '_blank', 'noopener,noreferrer');
    },
    []
  );

  if (loading) {
    return (
      <div className="pdp" aria-label="Loading product details">
        <div className="pdp__loading">
          <div className="pdp__spinner" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pdp" role="alert">
        <div className="pdp__error">
          <p>{error || 'Product not found'}</p>
          <button type="button" className="pdp__back-btn" onClick={handleBack}>
            ← Go back
          </button>
        </div>
      </div>
    );
  }

  const hasEnoughRatings = product.totalRatings >= 3;
  const hasLinks =
    purchaseData && (purchaseData.primaryAffiliate || purchaseData.links.length > 0);
  const primaryAffiliate = purchaseData?.primaryAffiliate;

  const slug = productSlug(product.brandName, product.productName);
  const productJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.productName,
    brand: { '@type': 'Brand', name: product.brandName },
    description: `${product.productName} by ${product.brandName}. ${product.thcContentMg}mg THC, ${product.volumeOz}oz ${product.beverageFormat}.${product.flavorProfile ? ' ' + product.flavorProfile : ''}`,
    ...(product.imageUrl ? { image: product.imageUrl } : {}),
    ...(product.priceUsd ? {
      offers: {
        '@type': 'Offer',
        price: product.priceUsd,
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
      },
    } : {}),
    ...((product.totalRatings ?? 0) >= 3 && (product as unknown as Record<string, unknown>).aggregateScore ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: (product as unknown as Record<string, unknown>).aggregateScore,
        ratingCount: product.totalRatings,
        bestRating: 5,
        worstRating: 1,
      },
    } : {}),
  };

  return (
    <div className="pdp">
      <SEO
        title={`${product.productName} by ${product.brandName} - ${product.thcContentMg}mg THC`}
        description={`${product.productName} by ${product.brandName}. ${product.thcContentMg}mg THC ${product.beverageFormat}, ${product.volumeOz}oz.${product.flavorProfile ? ' Flavor: ' + product.flavorProfile + '.' : ''} Read reviews and ratings.`}
        canonical={`/products/${id}/${slug}`}
        image={product.imageUrl}
        type="product"
        jsonLd={productJsonLd}
      />
      {/* Back button */}
      <button
        type="button"
        className="pdp__nav-back"
        onClick={handleBack}
        aria-label="Go back"
      >
        ←
      </button>

      {/* Hero image — full-width */}
      <div className="pdp__hero">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.productName}
            className="pdp__hero-image"
          />
        ) : (
          <div className="pdp__hero-placeholder" aria-hidden="true">
            <svg
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}
        {product.sponsored && (
          <span className="pdp__sponsored-label">Sponsored</span>
        )}
      </div>

      {/* Minimal info section: name, brand, THC, format, volume */}
      <section className="pdp__info" aria-label="Product information">
        <a href={`/thc-drinks?brand=${encodeURIComponent(product.brandName)}`} className="pdp__brand" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>{product.brandName}</a>
        <h1 className="pdp__name">{product.productName}</h1>
        <div className="pdp__meta">
          <span className="pdp__meta-item">{product.thcContentMg}mg THC</span>
          <span className="pdp__meta-divider" aria-hidden="true">·</span>
          <span className="pdp__meta-item">{product.beverageFormat}</span>
          <span className="pdp__meta-divider" aria-hidden="true">·</span>
          <span className="pdp__meta-item">{product.volumeOz} oz</span>
          {product.caloriesPerServing != null && (
            <>
              <span className="pdp__meta-divider" aria-hidden="true">·</span>
              <span className="pdp__meta-item">{product.caloriesPerServing} cal</span>
            </>
          )}
          {product.sugarGrams != null && (
            <>
              <span className="pdp__meta-divider" aria-hidden="true">·</span>
              <span className="pdp__meta-item">{product.sugarGrams}g sugar</span>
            </>
          )}
        </div>
      </section>

      {/* Score row: aggregate rating + buzz score + up/down — horizontal, compact */}
      <section className="pdp__scores" aria-label="Product scores">
        {hasEnoughRatings ? (
          <div className="pdp__score-row">
            <span
              className="pdp__score-item pdp__score-item--rating"
              aria-label={`Rating: ${product.aggregateScore} out of 5`}
            >
              ★ {product.aggregateScore?.toFixed(1)}
            </span>
            {product.aggregateBuzzScore != null && (
              <span
                className="pdp__score-item pdp__score-item--buzz"
                aria-label={`Buzz score: ${product.aggregateBuzzScore} out of 10`}
              >
                ⚡ {product.aggregateBuzzScore.toFixed(1)}
              </span>
            )}
            {product.aggregateUpDown && (
              <span
                className={`pdp__score-item pdp__score-item--effect pdp__score-item--${product.aggregateUpDown.toLowerCase()}`}
                aria-label={`Effect: ${product.aggregateUpDown}`}
              >
                {product.aggregateUpDown === 'Up'
                  ? '↑'
                  : product.aggregateUpDown === 'Down'
                    ? '↓'
                    : '↔'}{' '}
                {product.aggregateUpDown}
              </span>
            )}
            <span className="pdp__score-item pdp__score-item--count">
              ({product.totalRatings} ratings)
            </span>
          </div>
        ) : (
          <p className="pdp__no-ratings">Not enough ratings</p>
        )}
      </section>

      {/* Description */}
      {product.description && (
        <section className="pdp__description" aria-label="Product description">
          <p className="pdp__description-summary">{product.description}</p>
        </section>
      )}

      {/* Buy Now CTA — shown when brand has a buy website URL and all products are buyable */}
      {brand?.buyWebsiteUrl && brand?.allProductsBuyable && (
        <section className="pdp__buy-now" aria-label="Purchase">
          <button
            type="button"
            className="pdp__buy-now-btn"
            onClick={() => {
              const destUrl = brand.buyWebsiteUrl!;

              // Record click (fire-and-forget)
              fetch(`${API_BASE}/v1/clicks/buy`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  productId: product.productId,
                  brandName: product.brandName,
                  destinationUrl: destUrl,
                }),
              }).catch(() => {});

              // Open brand's buy URL (affiliate tracking baked into the URL itself)
              window.open(destUrl, '_blank', 'noopener,noreferrer');
            }}
          >
            Learn More & Purchase
          </button>
          <p className="pdp__buy-disclaimer">
            Age verification and compliance handled by the manufacturer's website.
          </p>
        </section>
      )}

      {/* Rating, review form & review history */}
      <ProductRatingReview productId={id!} productName={product.productName} />

      {/* Purchase CTA — sticky at bottom with gold accent */}
      <div className="pdp__cta-container">
        {hasLinks ? (
          <>
            {primaryAffiliate ? (
              <div className="pdp__cta-wrapper">
                {primaryAffiliate.discountCode && (
                  <p className="pdp__discount">
                    Use code <strong>{primaryAffiliate.discountCode}</strong>
                    {primaryAffiliate.discountDescription && (
                      <> — {primaryAffiliate.discountDescription}</>
                    )}
                  </p>
                )}
                <button
                  type="button"
                  className="pdp__cta-btn"
                  onClick={() => handlePurchaseClick(primaryAffiliate.linkId)}
                  aria-label={`Check price for ${product.productName} on brand site`}
                >
                  Check Price on Brand Site
                </button>
              </div>
            ) : (
              <div className="pdp__cta-wrapper">
                {purchaseData!.links.slice(0, 1).map((link) => (
                  <button
                    key={link.linkId}
                    type="button"
                    className="pdp__cta-btn"
                    onClick={() => handlePurchaseClick(link.linkId)}
                    aria-label={`Check price at ${link.sourceName}`}
                  >
                    Check Price at {link.sourceName}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="pdp__no-links">No purchase links available yet</p>
        )}

        {/* Affiliate disclosure — subtle, small text in footer */}
        {hasLinks && (
          <p className="pdp__disclosure">
            We may earn a commission when you purchase through links on this page.
          </p>
        )}
      </div>
    </div>
  );
}
